import { useCallback, useEffect, useRef, useState } from 'react';
import GetEventAverageUserWatchtime from '@/connection/analytics/get-event-average-user-watchtime';
import { IAnalyticsScorecard } from '@/types/analytics';
import GetLiveAndOnDemandEventViews from '@/connection/analytics/project-level/get-live-and-on-demand-event-views';
import { useAppSelector } from '@/store/hooks';
import { stageEnvironment } from '@/utils/urls';
import sleep from '@/utils/sleep';
import GetAverageProjectRating from '@/connection/analytics/project-level/get-average-project-rating';
import GetEventShares from '@/connection/analytics/project-level/get-event-shares';

export enum ESessionStatKeys {
  avgUserWatchtime = 'avgUserWatchtime',
  averageProjectRating = 'averageProjectRating',
  interactions = 'interactions',
  shares = 'shares',
}

export type TSessionStats = {
  [ESessionStatKeys.avgUserWatchtime]: IAnalyticsScorecard | null;
  [ESessionStatKeys.averageProjectRating]: IAnalyticsScorecard | null;
  [ESessionStatKeys.interactions]: IAnalyticsScorecard | null;
  [ESessionStatKeys.shares]: IAnalyticsScorecard | null;
};

type TFetchSessionStatsProps = {
  projectUuid?: string;
  sessionUuid?: string;
  language?: string;
  disabled?: boolean;
  stats?: Array<ESessionStatKeys>;
};

const useFetchSessionStats = ({
  projectUuid,
  sessionUuid,
  language,
  disabled,
  stats = [
    ESessionStatKeys.avgUserWatchtime,
    ESessionStatKeys.averageProjectRating,
    ESessionStatKeys.interactions,
    ESessionStatKeys.shares,
  ],
}: TFetchSessionStatsProps) => {
  const fetching = useRef(false);

  const createdDate = useAppSelector((state) => state.project.workingProject?.created_date);

  const abortControllersRef = useRef({
    avgUserWatchtimesController: new AbortController(),
    sharesController: new AbortController(),
    averageProjectRatingController: new AbortController(),
    interactionsController: new AbortController(),
  });

  const [data, setData] = useState<TSessionStats>({
    avgUserWatchtime: null,
    averageProjectRating: null,
    interactions: null,
    shares: null,
  });

  const abort = useCallback(() => {
    const controllers = abortControllersRef.current;
    fetching.current = false;
    Object.values(controllers).forEach((controller) => !controller.signal.aborted && controller.abort());
  }, []);

  const handleResponse = useCallback(
    (key: ESessionStatKeys) => (res: IAnalyticsScorecard | null) => {
      setData((prev) => ({ ...prev, [key]: { ...res, loading: false } }));
    },
    []
  );

  const handleLoad = useCallback((key: ESessionStatKeys) => {
    setData((prev) => ({ ...prev, [key]: { value: null, loading: true } }));
  }, []);

  const handleError = useCallback(
    (key: ESessionStatKeys) => (err: Error) => {
      console.error(`Error fetching ${key}:`, err);
      setData((prev) => ({ ...prev, [key]: null }));
    },
    []
  );

  useEffect(() => {
    if (disabled) return;
    if (!sessionUuid) return;
    if (!projectUuid) return;
    if (fetching.current) return;

    (async () => {
      try {
        fetching.current = true;
        const controllers = abortControllersRef.current;

        const start = new Date(createdDate || 0).valueOf();

        if (stats.includes(ESessionStatKeys.avgUserWatchtime)) {
          handleLoad(ESessionStatKeys.avgUserWatchtime);
          GetEventAverageUserWatchtime({ projectUuid, start, signal: controllers.avgUserWatchtimesController.signal })
            .then(handleResponse(ESessionStatKeys.avgUserWatchtime))
            .catch(handleError(ESessionStatKeys.avgUserWatchtime));

          await sleep(300); // avoid sending all requests at once, which can cause server overload
        }

        // Note: this is "interactions" which is really just "page views"
        if (stats.includes(ESessionStatKeys.interactions)) {
          handleLoad(ESessionStatKeys.interactions);
          GetLiveAndOnDemandEventViews({ projectUuid, start, signal: controllers.interactionsController.signal })
            .then(handleResponse(ESessionStatKeys.interactions))
            .catch(handleError(ESessionStatKeys.interactions));
          await sleep(300); // avoid sending all requests at once, which can cause server overload
        }

        if (stats.includes(ESessionStatKeys.shares)) {
          handleLoad(ESessionStatKeys.shares);
          GetEventShares({
            projectUuid,
            start,
            end: Date.now(),
            signal: controllers.sharesController.signal,
          })
            .then(handleResponse(ESessionStatKeys.shares))
            .catch(handleError(ESessionStatKeys.shares));
          await sleep(300); // avoid sending all requests at once, which can cause server overload
        }

        if (stats.includes(ESessionStatKeys.averageProjectRating)) {
          handleLoad(ESessionStatKeys.averageProjectRating);
          GetAverageProjectRating({ projectUuid, start, signal: controllers.averageProjectRatingController.signal })
            .then(handleResponse(ESessionStatKeys.averageProjectRating))
            .catch(handleError(ESessionStatKeys.averageProjectRating));
        }
      } catch (error) {
        console.error('Failed to fetch project stats:', error);
      }
    })();

    return () => {
      // Note: on localhost (development mode), this cleanup always triggers because React.StrictMode forces hooks to trigger twice.
      // So the data on localhost is always null, therefore I'm not calling it on localhost.
      // It does not do so in remote environments.
      if (!stageEnvironment.isLocal) {
        //abort();
      }
    };
  }, [
    language,
    projectUuid,
    createdDate,
    abort,
    disabled,
    sessionUuid,
    stats,
    handleLoad,
    handleResponse,
    handleError,
  ]);

  return { data, abort };
};

export default useFetchSessionStats;
